import {
  Button,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Spinner,
  Label,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import * as React from "react";
import { useContext, useEffect } from "react";
import { Constants } from "../../common/constants";
import { TeamcenterAuthenticationProps } from "../../types/CommonTypes";
import { TCTeamsFxContext } from "../TCTeamsContext";
import config from "../../config/config";
import { logger } from "../../Logger";

// This is the dialog used to login from Teams to Teamcenter
const TeamcenterAuthentication: React.FC<TeamcenterAuthenticationProps> = ({
  teamsContext,
  error,
  checkingOnboardedStatus,
  isOnboarded,
  siteOptions,
  selectedSite,
  onboardedStatusFailed,
  setSelectedSite,
  isSiteOptionsLoaded,
  setIsAuthenticated,
  setOpen,
  seti18n,
}) => {
  const tcTeamsContext = useContext(TCTeamsFxContext);
  const { setError } = tcTeamsContext.failure;
  if (error) {
    setError(error);
  }
  const i18n = tcTeamsContext.i18n;

  useEffect(() => {
    setIsAuthenticated(tcTeamsContext?.teamcenter?.isAuthenticated);
  }, [tcTeamsContext?.teamcenter?.isAuthenticated]);

  useEffect(() => {
    seti18n(tcTeamsContext.i18n);
  }, [tcTeamsContext.i18n]);

  // Launch the login process
  const onLoginHandler = async () => {
    logger.logTrace("TeamcenterAuthentication - onLoginHandler called");
    //Before login then clear local cache if exists.
    window.localStorage.removeItem(Constants.cacheTeamcenterCode);
    window.localStorage.removeItem(Constants.cacheConfiguration);
    window.localStorage.removeItem(Constants.cacheUserConfiguration);
    setOpen(false);
    if (selectedSite) {
      window.localStorage.setItem(
        Constants.cacheSelectedSite,
        JSON.stringify(selectedSite)
      );
    }
    await tcTeamsContext?.teamcenter.login();
    logger.logTrace("TeamcenterAuthentication - onLoginHandler completed");
  };

  return (
    <>
      <div className="text-center">
        {!checkingOnboardedStatus && isOnboarded && (
          <div>
            <div className="login-popup">{i18n.GetStartedText}</div>
            <div className="div-button-dropdown-label">
              {siteOptions?.length > 1 && (
                <div className="div-label-dropdown">
                  <Label className="label-for-dropdown">
                    {i18n.SelectAnEnvironment}:
                  </Label>
                  <Dropdown
                    placeholder=""
                    value={
                      selectedSite?.siteDisplay ||
                      siteOptions[0]?.siteDisplay ||
                      ""
                    }
                    className="dropdown-for-environment"
                    listbox={{ className: "dropdown-listbox" }}
                    onOptionSelect={(event, data) => {
                      const selectedSite = siteOptions.find(
                        (site) => site.siteId === data.optionValue
                      );
                      setSelectedSite(selectedSite || null);
                    }}
                    disabled={
                      siteOptions.length === 0 ||
                      tcTeamsContext?.teamcenter?.isAuthenticating ||
                      false
                    }
                  >
                    {siteOptions.map((option) => (
                      <Option key={option.siteId} value={option.siteId}>
                        {option.siteDisplay}
                      </Option>
                    ))}
                  </Dropdown>
                </div>
              )}
              <Button
                appearance="primary"
                className="login-button-width"
                onClick={onLoginHandler}
                disabled={
                  onboardedStatusFailed ||
                  tcTeamsContext?.teamcenter?.isAuthenticating ||
                  false
                }
              >
                {tcTeamsContext?.teamcenter?.isAuthenticating && (
                  <div className="flex">
                    <Spinner size="tiny" className="login-button" />
                  </div>
                )}
                {i18n.LoginTeamcenterText}
              </Button>
            </div>
          </div>
        )}
        {!checkingOnboardedStatus && !isOnboarded && (
          <div>
            <b>{i18n.Teamcenter}</b>
            {i18n.NotOnBoardedText}
            <a
              href="https://support.sw.siemens.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n.ContactUsText}
            </a>
          </div>
        )}
      </div>
      {config.environment === Constants.development &&
        tcTeamsContext?.failure?.error && (
          <Accordion collapsible>
            <AccordionItem value="1">
              <AccordionHeader>{i18n.ErrorDetailsText}:</AccordionHeader>
              <AccordionPanel>
                {i18n.CorrelationIDText}:{" "}
                {tcTeamsContext?.failure?.error?.correlationId}
              </AccordionPanel>
              <AccordionPanel>
                {i18n.ErrorCodeText}:{" "}
                {tcTeamsContext?.failure?.error?.statusCode}
              </AccordionPanel>
              <AccordionPanel>
                {i18n.ErrorMessageText}:{" "}
                {tcTeamsContext?.failure?.error?.message}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
    </>
  );
};

export default TeamcenterAuthentication;
